import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import Button from '../../components/common/Button/Button';
import styles from './AddOffer.module.css';

const initialFormData = {
    offer_id: '',
    offer_name: '',
    offer_code: '',
    terms_and_conditions: '',
    calculation_type: 'percentage',
    offer_percentage: 0,
    offer_value: 0,
    active_timestamp: '',
    deactive_timestamp: '',
    status: 'active',
    max_count: 0,
    minimum_order_amount: 0,
    max_value: 0,
    consumed_count: 0,
    consumed_value: 0,
    created_at: '',
    updated_at: '',
    display: true,
};

const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const AddOffer = ({ offerId, onSubmitted, onCancel }) => {
    const id = offerId;
    const isEditing = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [fetchingDetail, setFetchingDetail] = useState(isEditing);

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        if (!isEditing) return;

        setFetchingDetail(true);
        api.get(`/offer/${id}`)
            .then(response => {
                let dataSource = response?.result ?? response?.data ?? response ?? {};

                if (typeof dataSource === 'string') {
                    try {
                        dataSource = JSON.parse(dataSource);
                    } catch (error) {
                        console.error('Invalid offer response JSON:', error);
                    }
                }

                if (
                    dataSource &&
                    typeof dataSource === 'object' &&
                    !Array.isArray(dataSource) &&
                    typeof dataSource.result !== 'undefined'
                ) {
                    dataSource = dataSource.result;
                }

                const data = Array.isArray(dataSource) ? (dataSource[0] || {}) : dataSource;
                if (!data || (!data.offer_id && String(data.offer_id) !== '0')) {
                    toast.error("Offer not found");
                    if (onCancel) onCancel();
                    return;
                }

                setFormData({
                    offer_id: data.offer_id ?? '',
                    offer_name: data.offer_name || '',
                    offer_code: data.offer_code || '',
                    terms_and_conditions: data.terms_and_conditions || '',
                    calculation_type: data.calculation_type || 'percentage',
                    offer_percentage: data.offer_percentage ?? 0,
                    offer_value: data.offer_value ?? 0,
                    active_timestamp: formatDateTime(data.active_timestamp),
                    deactive_timestamp: formatDateTime(data.deactive_timestamp),
                    status: data.status || 'active',
                    max_count: data.max_count ?? 0,
                    minimum_order_amount: data.minimum_order_amount ?? 0,
                    max_value: data.max_value ?? 0,
                    consumed_count: data.consumed_count ?? 0,
                    consumed_value: data.consumed_value ?? 0,
                    created_at: formatDateTime(data.created_at),
                    updated_at: formatDateTime(data.updated_at),
                    display: data.display !== false,
                });
            })
            .catch(err => {
                console.error("Failed to load offer details", err);
                toast.error(err?.data?.message || err?.message || "Failed to load offer details");
            })
            .finally(() => {
                setFetchingDetail(false);
            });
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'display'
                ? value === 'true'
                : type === 'checkbox'
                    ? checked
                    : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.offer_name || !formData.offer_code || !formData.active_timestamp) {
            toast.error("Offer name, offer code, and active timestamp are required.");
            return;
        }

        if (formData.calculation_type === 'percentage' && (Number(formData.offer_percentage) <= 0 || Number(formData.offer_percentage) > 100)) {
            toast.error("Percentage must be between 1 and 100.");
            return;
        }

        if (formData.calculation_type === 'flat' && Number(formData.offer_value) <= 0) {
            toast.error("Flat value must be greater than 0.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                offer_name: formData.offer_name,
                offer_code: formData.offer_code,
                terms_and_conditions: formData.terms_and_conditions || null,
                calculation_type: formData.calculation_type,
                offer_percentage: Number(formData.offer_percentage || 0),
                offer_value: Number(formData.offer_value || 0),
                active_timestamp: formData.active_timestamp || null,
                deactive_timestamp: formData.deactive_timestamp || null,
                status: formData.status,
                max_count: Number(formData.max_count || 0),
                minimum_order_amount: Number(formData.minimum_order_amount || 0),
                max_value: Number(formData.max_value || 0),
                consumed_count: Number(formData.consumed_count || 0),
                consumed_value: Number(formData.consumed_value || 0),
                display: Boolean(formData.display),
            };

            if (isEditing) {
                await api.put(`/offer/${id}`, payload);
                toast.success("Offer updated successfully!");
            } else {
                await api.post('/offer', payload);
                toast.success("Offer created successfully!");
            }

            setTimeout(() => {
                if (onSubmitted) onSubmitted();
            }, 1000);
        } catch (error) {
            console.error("Error saving offer:", error);
            const errMsg = error?.data?.message || error?.message || "Failed to save offer";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="top-center" autoClose={2000} />

            <div className={styles.card}>

                {fetchingDetail ? (
                    <div className={styles.loadingState}>Loading offer details...</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Offer ID</label>
                                <input
                                    type="number"
                                    name="offer_id"
                                    value={formData.offer_id}
                                    className={styles.input}
                                    disabled
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Offer Name <span className={styles.required}>*</span></label>
                                <input
                                    type="text"
                                    name="offer_name"
                                    value={formData.offer_name}
                                    onChange={handleChange}
                                    placeholder="e.g. Welcome Discount"
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Offer Code <span className={styles.required}>*</span></label>
                                <input
                                    type="text"
                                    name="offer_code"
                                    value={formData.offer_code}
                                    onChange={handleChange}
                                    placeholder="e.g. SAVE10"
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Calculation Type <span className={styles.required}>*</span></label>
                                <select
                                    name="calculation_type"
                                    value={formData.calculation_type}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="percentage">Percentage</option>
                                    <option value="flat">Flat Number</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Offer Percentage (%)</label>
                                <input
                                    type="number"
                                    name="offer_percentage"
                                    value={formData.offer_percentage}
                                    onChange={handleChange}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Offer Value (Rs)</label>
                                <input
                                    type="number"
                                    name="offer_value"
                                    value={formData.offer_value}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active From <span className={styles.required}>*</span></label>
                                <input
                                    type="datetime-local"
                                    name="active_timestamp"
                                    value={formData.active_timestamp}
                                    onChange={handleChange}
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Active To</label>
                                <input
                                    type="datetime-local"
                                    name="deactive_timestamp"
                                    value={formData.deactive_timestamp}
                                    onChange={handleChange}
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Status <span className={styles.required}>*</span></label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Max Count</label>
                                <input
                                    type="number"
                                    name="max_count"
                                    value={formData.max_count}
                                    onChange={handleChange}
                                    min="0"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Minimum Order Amount</label>
                                <input
                                    type="number"
                                    name="minimum_order_amount"
                                    value={formData.minimum_order_amount}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Max Value</label>
                                <input
                                    type="number"
                                    name="max_value"
                                    value={formData.max_value}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Consumed Count</label>
                                <input
                                    type="number"
                                    name="consumed_count"
                                    value={formData.consumed_count}
                                    onChange={handleChange}
                                    min="0"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Consumed Value</label>
                                <input
                                    type="number"
                                    name="consumed_value"
                                    value={formData.consumed_value}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Created At</label>
                                <input
                                    type="datetime-local"
                                    name="created_at"
                                    value={formData.created_at}
                                    className={styles.input}
                                    disabled
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Updated At</label>
                                <input
                                    type="datetime-local"
                                    name="updated_at"
                                    value={formData.updated_at}
                                    className={styles.input}
                                    disabled
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Show in user's UI</label>
                                <select
                                    name="display"
                                    value={String(formData.display)}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="true">Visible</option>
                                    <option value="false">Hidden</option>
                                </select>
                            </div>

                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.label}>Terms and Conditions</label>
                                <textarea
                                    name="terms_and_conditions"
                                    value={formData.terms_and_conditions}
                                    onChange={handleChange}
                                    placeholder="Enter specific terms for this offer..."
                                    className={styles.input}
                                />
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <Button
                                variant="secondary"
                                onClick={onCancel}
                                type="button"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Saving..." : (isEditing ? "Update Offer" : "Save Offer")}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddOffer;
